import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  CommonModule,
  NgOptimizedImage,
  ViewportScroller,
} from '@angular/common';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  PublicCartAttributeName,
  PublicCartPaymentMethod,
  PublicCartService,
  PublicCheckoutFormData,
  PublicCheckoutFormGroup,
  PublicCheckoutShippingFormControls,
  ShippingMethod,
} from '@web/shared/module/shop';
import {
  blank,
  executeOrNull,
  Guid,
  isNil,
  Nillable,
  once,
  valueOrDefault,
} from '@web/utils';
import {
  AlertsComponent,
  AlertsService,
  Change,
  CheckboxWithLabelComponent,
  companyNameAndNipCrossValidation,
  EmailValidator,
  FormChangeWatcherService,
  FormControlComponent,
  ImageThumbnailComponent,
  InPostPointLocation,
  InPostPointSelect,
  InPostPointSelectComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  PrimengTextareaDirective,
  SelectItem,
  SpinnerFilledComponent,
  validateInDepth,
} from '@web/shared/ui';
import { Textarea } from 'primeng/textarea';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import {
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  PhoneNumberWithPrefix,
  PhoneNumberWithPrefixComponent,
  PublicDictionaryCountrySelectItemsProviderService,
} from '@web/shared/module/dictionary';
import { Select } from 'primeng/select';
import { RadioButton } from 'primeng/radiobutton';
import { SummernoteHtmlPreviewComponent } from '@web/shared/external';
import { PublicCheckoutSubmitSuccessEvent } from '../../events/public-checkout-submit-success.event';
import { catchIfUnprocessableEntity } from '@web/common';
import { PublicCheckoutApiFieldsMapping } from '../../types/public-checkout-api-fields-mapping';
import { PublicCheckoutHttpErrorHandler } from '../../api/handlers/public-checkout-http-error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  NewsletterCheckboxLabelComponent,
  PrivacyPolicyClauseComponent,
  RegulationsCheckboxLabelComponent,
} from '@web/shared/module/auth';

@Component({
  selector: 'app-public-checkout-form',
  imports: [
    Button,
    Checkbox,
    CheckboxWithLabelComponent,
    CommonModule,
    FormControlComponent,
    ImageThumbnailComponent,
    InPostPointSelectComponent,
    InputText,
    NgOptimizedImage,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    PrimengTextareaDirective,
    RadioButton,
    ReactiveFormsModule,
    Select,
    SummernoteHtmlPreviewComponent,
    Textarea,
    AlertsComponent,
    SpinnerFilledComponent,
    NewsletterCheckboxLabelComponent,
    PrivacyPolicyClauseComponent,
    RegulationsCheckboxLabelComponent,
    PhoneNumberWithPrefixComponent,
  ],
  templateUrl: './public-checkout-form.component.html',
  styleUrl: './public-checkout-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class PublicCheckoutFormComponent implements OnInit, OnDestroy {
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly dictionaryCountryDataSource = inject(PublicDictionaryCountrySelectItemsProviderService);
  private readonly cart = inject(PublicCartService);
  private readonly formChangeWatcher = inject(FormChangeWatcherService);
  private readonly alertsService = inject(AlertsService);
  /* prettier-ignore */
  private readonly checkoutHttpErrorHandler = inject(PublicCheckoutHttpErrorHandler);

  /* prettier-ignore */
  public readonly submitSuccessEvent = output<PublicCheckoutSubmitSuccessEvent>();

  protected readonly form: PublicCheckoutFormGroup;
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly countrySelectItems$: Observable<SelectItem<Guid>[]>;
  /* prettier-ignore */
  protected readonly shippingMethodSelectItems$: Observable<SelectItem<Guid>[]>;
  protected readonly paymentMethods$: Observable<PublicCartPaymentMethod[]>;
  protected readonly shippingStateControlAvailability$: Observable<boolean>;
  protected readonly billingStateControlAvailability$: Observable<boolean>;
  protected readonly valueOrDefault = valueOrDefault;
  protected readonly currentCountryCode$: Observable<Nillable<string>>;
  /* prettier-ignore */
  protected readonly currentShippingMethod$: Observable<Nillable<ShippingMethod>>;
  /* prettier-ignore */
  protected readonly currentPaymentMethod$: Observable<Nillable<PublicCartPaymentMethod>>;
  protected readonly shippingMethodHint$: Observable<Nillable<string>>;
  /* prettier-ignore */
  protected readonly shippingAddressVisibility = signal<boolean>(false);
  protected readonly checkoutPending = signal<boolean>(false);

  /* prettier-ignore */
  private readonly fieldMapping = new Map<string, PublicCheckoutApiFieldsMapping>([
    ['billingAddress.firstName', { apiPath: 'billingAddress.firstName', violationPropertyPathLabel: 'Imię' }],
    ['billingAddress.lastName', { apiPath: 'billingAddress.lastName', violationPropertyPathLabel: 'Nazwisko' }],
    ['billingAddress.companyName', { apiPath: 'billingAddress.companyName', violationPropertyPathLabel: 'Nazwa firmy' }],
    ['billingAddress.companyNip', { apiPath: 'billingAddress.companyNip', violationPropertyPathLabel: 'NIP' }],
    ['billingAddress.streetLine1', { apiPath: 'billingAddress.streetLine1', violationPropertyPathLabel: 'Adres' }],
    ['billingAddress.streetLine2', { apiPath: 'billingAddress.streetLine2', violationPropertyPathLabel: 'Adres cd' }],
    ['billingAddress.postCode', { apiPath: 'billingAddress.postCode', violationPropertyPathLabel: 'Kod pocztowy' }],
    ['billingAddress.city', { apiPath: 'billingAddress.city', violationPropertyPathLabel: 'Miasto' }],
    ['billingAddress.phone', { apiPath: 'billingAddress.phone', violationPropertyPathLabel: 'Telefon' }],
    ['billingAddress.phonePrefix', { apiPath: 'billingAddress.phonePrefix', violationPropertyPathLabel: 'Prefiks numeru telefonu' }],
    ['billingAddress.email', { apiPath: 'billingAddress.email', violationPropertyPathLabel: 'Adres e-mail' }],
    ['billingAddress.countryId', { apiPath: 'billingAddress.country', violationPropertyPathLabel: 'Kraj' }],
    ['billingAddress.state', { apiPath: 'billingAddress.state', violationPropertyPathLabel: 'Region' } ],
    ['shippingAddress.firstName', { apiPath: 'shippingAddress.firstName', violationPropertyPathLabel: 'Imię (dane do wysyłki)' }],
    ['shippingAddress.lastName', { apiPath: 'shippingAddress.lastName', violationPropertyPathLabel: 'Nazwisko (dane do wysyłki)' }],
    ['shippingAddress.companyName', { apiPath: 'shippingAddress.companyName', violationPropertyPathLabel: 'Nazwa firmy (dane do wysyłki)' }],
    ['shippingAddress.streetLine1', { apiPath: 'shippingAddress.streetLine1', violationPropertyPathLabel: 'Adres (dane do wysyłki)' }],
    ['shippingAddress.streetLine2', { apiPath: 'shippingAddress.streetLine2', violationPropertyPathLabel: 'Adres cd. (dane do wysyłki)' }],
    ['shippingAddress.postCode', { apiPath: 'shippingAddress.postCode', violationPropertyPathLabel: 'Kod pocztowy (dane do wysyłki)' }],
    ['shippingAddress.city', { apiPath: 'shippingAddress.city', violationPropertyPathLabel: 'Miasto (dane do wysyłki)' }],
    ['shippingAddress.phone', { apiPath: 'shippingAddress.phone', violationPropertyPathLabel: 'Telefon (dane do wysyłki)' }],
    ['shippingAddress.phonePrefix', { apiPath: 'shippingAddress.phonePrefix', violationPropertyPathLabel: 'Prefiks numeru telefonu (dane do wysyłki)' }],
    ['shippingAddress.email', { apiPath: 'shippingAddress.email', violationPropertyPathLabel: 'Adres e-mail (dane do wysyłki)' }],
    ['shippingAddress.countryId', { apiPath: 'shippingAddress.country', violationPropertyPathLabel: 'Kraj (dane do wysyłki)' }],
    ['shippingAddress.state', { apiPath: 'shippingAddress.state', violationPropertyPathLabel: 'Region (dane do wysyłki)' }],
    ['comment', { apiPath: 'comment', violationPropertyPathLabel: 'Uwagi do zamówienia' }],
    ['otherShippingAddress', { apiPath: 'otherShippingAddress', violationPropertyPathLabel: 'Inny adres wysyłki' }],
    ['shippingMethodId', { apiPath: 'shippingMethod', violationPropertyPathLabel: 'Metoda dostawy' }],
    ['shippingPoint', { apiPath: 'shippingPoint', violationPropertyPathLabel: 'Punkt odbioru' }],
    ['paymentMethod', { apiPath: 'paymentMethod', violationPropertyPathLabel: 'Metoda płatności' }],
  ]);

  /* prettier-ignore */
  public constructor() {
    this.form = this.buildForm();
    this.countrySelectItems$ = this.prepareCountrySelectItems();
    this.shippingMethodSelectItems$ = this.prepareShippingMethodSelectItems();
    this.paymentMethods$ = this.preparePaymentMethods();
    this.currentShippingMethod$ = this.prepareCurrentShippingMethodSource();
    this.currentPaymentMethod$ = this.prepareCurrentPaymentMethodSource();
    this.currentCountryCode$ = this.prepareCurrentCountryCodeSource();
    this.shippingStateControlAvailability$ = this.prepareShippingStateControlAvailabilitySource();
    this.shippingMethodHint$ = this.prepareShippingMethodHintSource(this.currentCountryCode$);
    this.billingStateControlAvailability$ = this.prepareBillingStateControlAvailabilitySource();
  }

  public ngOnInit(): void {
    this.tryToFillFormByShippingDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.otherShippingValueChanges();
        this.watchControlsValueChanges();
        this.configureCompanyCrossValidation();
      });
  }

  public ngOnDestroy(): void {
    this.formChangeWatcher.destroy();
  }

  public handleSubmitClick(): void {
    this.alertsService.clear();

    this.markCheckoutAsPending();

    if (validateInDepth(this.form)) {
      const {
        billingAddress,
        shippingAddress,
        otherShippingAddress,
        comment,
        shippingMethodId,
        paymentMethod,
        shippingPoint,
        newsletter,
        regulations,
      } = this.form.getRawValue();
      this.cart
        .checkoutByFormData(
          new PublicCheckoutFormData({
            newsletter: newsletter,
            regulations: regulations,
            otherShippingAddress: otherShippingAddress,
            billingAddress: {
              city: billingAddress.city,
              companyName: billingAddress.companyName,
              companyNip: billingAddress.companyNip,
              countryId: valueOrDefault(billingAddress.countryId, ''),
              email: billingAddress.email,
              firstName: billingAddress.firstName,
              lastName: billingAddress.lastName,
              phone: billingAddress.phone,
              postCode: billingAddress.postCode,
              state: billingAddress.state,
              streetLine1: billingAddress.streetLine1,
              streetLine2: billingAddress.streetLine2,
            },
            shippingAddress: otherShippingAddress
              ? {
                  city: shippingAddress.city,
                  companyName: shippingAddress.companyName,
                  countryId: valueOrDefault(shippingAddress.countryId, ''),
                  email: shippingAddress.email,
                  firstName: shippingAddress.firstName,
                  lastName: shippingAddress.lastName,
                  phone: shippingAddress.phone,
                  postCode: shippingAddress.postCode,
                  state: shippingAddress.state,
                  streetLine1: shippingAddress.streetLine1,
                  streetLine2: shippingAddress.streetLine2,
                }
              : null,
            comment: comment,
            shippingMethodId: valueOrDefault(shippingMethodId, ''),
            paymentMethod: valueOrDefault(paymentMethod, ''),
            shippingPoint: shippingPoint,
          }),
        )
        .pipe(
          finalize(() => this.markCheckoutAsResolved()),
          catchIfUnprocessableEntity((response) => {
            this.showFailureAlertByResponse(response);
            this.scrollToTop();

            return EMPTY;
          }),
        )
        .subscribe({
          next: (checkout) => {
            this.markCheckoutAsResolved();

            this.submitSuccessEvent.emit(
              new PublicCheckoutSubmitSuccessEvent({
                orderId: checkout.orderId,
                accessToken: checkout.accessToken,
              }),
            );
          },
        });
    } else {
      this.markCheckoutAsResolved();
    }
  }

  private configureCompanyCrossValidation(): void {
    const companyNip = this.form.controls.billingAddress.controls.companyNip;
    const companyName = this.form.controls.billingAddress.controls.companyName;

    companyNameAndNipCrossValidation({
      companyNipControl: companyNip,
      companyNameControl: companyName,
      destroyRef: this.destroyRef,
    });
  }

  private markCheckoutAsPending(): void {
    this.checkoutPending.set(true);
  }

  private markCheckoutAsResolved(): void {
    this.checkoutPending.set(false);
  }

  /* prettier-ignore */
  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = valueOrDefault(this.checkoutHttpErrorHandler.extract({ mappings: this.fieldMapping, response }), []);

    this.alertsService.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }

  private scrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  private watchControlsValueChanges(): void {
    this.formChangeWatcher
      .watch(this.form)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((change) => {
        if (this.shouldExcludeChangeDetection(change)) {
          return;
        }

        const pathName = this.fieldMapping.get(change.path)?.apiPath;

        if (!isNil(pathName)) {
          this.updateProperty(pathName, change)
            .pipe(once())
            .subscribe({
              next: () => {
                this.refreshShippingMethodsAfterChange(change);
                this.refreshShoppingCartDetailsAfterChange(change);
                this.refreshPaymentMethodsAfterChange(change);
              },
            });
        }
      });
  }

  /* prettier-ignore */
  private updateProperty(pathName: PublicCartAttributeName, change: Change): Observable<void> {
    const {value} = change.control;

    const correctnessValue = <T>(value: T) => {
      if (typeof value ===  'boolean') {
        return value;
      }

      if (['shippingPoint'].includes(pathName)) {
        const point = value as InPostPointSelect;

        return point?.name;
      }

      return value;
    }

    if (['billingAddress.phone'].includes(pathName)) {
      const phoneNumberWithPrefix = value as Nillable<PhoneNumberWithPrefix>;

      return this.cart
        .updateProperty('billingAddress.phonePrefix', phoneNumberWithPrefix?.prefix)
        .pipe(switchMap(() => this.cart
          .updateProperty('billingAddress.phone', phoneNumberWithPrefix?.number)
        ));
    }

    if (['shippingAddress.phone'].includes(pathName)) {
      const phoneNumberWithPrefix = value as Nillable<PhoneNumberWithPrefix>;

      return this.cart
        .updateProperty('shippingAddress.phonePrefix', phoneNumberWithPrefix?.prefix)
        .pipe(switchMap(() => this.cart
          .updateProperty('shippingAddress.phone', phoneNumberWithPrefix?.number)
        ));
    }

    return this.cart
      .updateProperty(pathName, correctnessValue(value))
  }

  private tryToFillFormByShippingDetails(): Observable<void> {
    return this.cart.details$.pipe(
      once(),
      catchError(() => of(null)),
      tap((details) => {
        if (isNil(details)) {
          return;
        }

        const { shippingAddress, billingAddress, shipping } = details;

        /* prettier-ignore */
        this.form.patchValue({
          shippingPoint: executeOrNull(shipping?.method?.inPostGeowidgetProperty, (result) => {
            if (isNil(result.longitude) || isNil(result.latitude)) {
              return null;
            }

            return new InPostPointSelect({
              name: result.value,
              description: result.description,
              location: new InPostPointLocation({
                longitude: result.longitude,
                latitude: result.latitude,
              })
            })
          }),
          otherShippingAddress: valueOrDefault(details?.otherShippingAddress, false),
          comment: details.comment,
          shippingMethodId: valueOrDefault(details?.shipping.method?.id, null),
          billingAddress: {
            streetLine1: valueOrDefault(billingAddress?.streetLine1, ''),
            streetLine2: valueOrDefault(billingAddress?.streetLine2, null),
            city: valueOrDefault(billingAddress?.city, ''),
            companyName: valueOrDefault(billingAddress?.companyName, null),
            countryId: valueOrDefault(billingAddress?.country?.id, null),
            state: valueOrDefault(billingAddress?.state, null),
            email: valueOrDefault(billingAddress?.email, ''),
            firstName: valueOrDefault(billingAddress?.firstName, ''),
            lastName: valueOrDefault(billingAddress?.lastName, ''),
            companyNip: valueOrDefault(billingAddress?.companyNip, null),
            phone: billingAddress?.phone,
            postCode: valueOrDefault(billingAddress?.postCode, ''),
          },
          shippingAddress: {
            streetLine1: valueOrDefault(shippingAddress?.streetLine1, ''),
            streetLine2: valueOrDefault(shippingAddress?.streetLine2, null),
            city: valueOrDefault(shippingAddress?.city, ''),
            companyName: valueOrDefault(shippingAddress?.companyName, null),
            countryId: valueOrDefault(shippingAddress?.country?.id, null),
            state: valueOrDefault(shippingAddress?.state, null),
            email: valueOrDefault(shippingAddress?.email, ''),
            firstName: valueOrDefault(shippingAddress?.firstName, ''),
            lastName: valueOrDefault(shippingAddress?.lastName, ''),
            phone: shippingAddress?.phone,
            postCode: valueOrDefault(shippingAddress?.postCode, ''),
          },
        });
      }),
      blank(),
    );
  }

  private prepareCountrySelectItems(): Observable<SelectItem<Guid>[]> {
    return this.dictionaryCountryDataSource.fetchSelectItems('id');
  }

  private prepareShippingMethodSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.cart.shippingMethods$;
  }

  private preparePaymentMethods(): Observable<PublicCartPaymentMethod[]> {
    return this.cart.paymentMethods$;
  }

  /* prettier-ignore */
  private prepareCurrentShippingMethodSource(): Observable<Nillable<ShippingMethod>> {
    return defer(() => this.form.controls.shippingMethodId.valueChanges.pipe(
      startWith(this.form.controls.shippingMethodId.value),
      switchMap((id) => isNil(id) ? of(null) : this.cart.fetchShippingMethodById(id)),
      tap(method => {
        const { shippingPoint } = this.form.controls;

        if (isNil(method)) {
          shippingPoint.disable({ emitEvent: false });

          return;
        }

        if ([method.isDomesticInPostGeowidget, method.isInternationalInPostGeowidget].some((result) => result)) {
          shippingPoint.enable({ emitEvent: false });

          return;
        }

        shippingPoint.disable({ emitEvent: false });
      })
    ));
  }

  /* prettier-ignore */
  private prepareCurrentPaymentMethodSource(): Observable<Nillable<PublicCartPaymentMethod>> {
    return defer(() =>
      this.form.controls.paymentMethod.valueChanges.pipe(
        startWith(this.form.controls.paymentMethod.value),
        switchMap((id) => isNil(id) ? of(null) : this.cart.fetchPaymentMethodById(id)),
      ),
    );
  }

  private prepareCurrentCountryCodeSource(): Observable<Nillable<string>> {
    return defer(() =>
      this.form.controls.otherShippingAddress.valueChanges.pipe(
        startWith(this.form.controls.otherShippingAddress.value),
        distinctUntilChanged(),
        switchMap((otherShippingAddress) => {
          const selectedControl = otherShippingAddress
            ? this.form.controls.shippingAddress.controls.countryId
            : this.form.controls.billingAddress.controls.countryId;

          return selectedControl.valueChanges.pipe(
            startWith(selectedControl.value),
          );
        }),
        switchMap((countryId) => {
          return isNil(countryId)
            ? of(null)
            : this.dictionaryCountryDataSource.fetchIsoCodeForId(countryId);
        }),
      ),
    );
  }

  /* prettier-ignore */
  private prepareShippingStateControlAvailabilitySource(): Observable<boolean> {
    const { countryId, state } = this.form.controls.shippingAddress.controls;

    return this.stateAvailabilityChanges(countryId).pipe(
      tap((isAvailable) => isAvailable ? state.enable({ emitEvent: false }) : state.disable({ emitEvent: false })),
    );
  }

  /* prettier-ignore */
  private prepareShippingMethodHintSource(currentCountryCode$: Observable<Nillable<string>>): Observable<Nillable<string>> {
    return defer(()=> currentCountryCode$.pipe(
      map((code) => !isNil(code)),
      tap((availability) => {
        const { shippingMethodId } = this.form.controls;

        availability
          ? shippingMethodId.enable({ emitEvent: false })
          : shippingMethodId.disable({ emitEvent: false });
      }),
      map((availability) => availability ? null : 'Aby wybrać rodzaj wysyłki, należy najpierw wskazać kraj.'),
    ));
  }

  private prepareBillingStateControlAvailabilitySource(): Observable<boolean> {
    const { countryId, state } = this.form.controls.billingAddress.controls;

    return this.stateAvailabilityChanges(countryId).pipe(
      tap((isAvailable) =>
        isAvailable
          ? state.enable({ emitEvent: false })
          : state.disable({ emitEvent: false }),
      ),
    );
  }

  private otherShippingValueChanges(): void {
    this.form.controls.otherShippingAddress.valueChanges
      .pipe(
        startWith(this.form.controls.otherShippingAddress.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((enable) => {
        this.shippingAddressVisibility.set(enable);
        this.enableOrDisableShipping(enable);
      });
  }

  private buildForm(): PublicCheckoutFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      newsletter: this.builder.nonNullable.control<boolean>(false),
      regulations: this.builder.nonNullable.control<boolean>(false, [Validators.required, Validators.requiredTrue]),
      shippingPoint: this.builder.control<Nillable<InPostPointSelect>>(null, [Validators.required]),
      billingAddress: this.builder.group({
        city: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.cityMaxLength)]),
        companyName: this.builder.control<Nillable<string>>('', [Validators.maxLength(PublicCheckoutFormData.companyNameMaxLength)]),
        companyNip: this.builder.control<Nillable<string>>('', [Validators.maxLength(PublicCheckoutFormData.companyNipMaxLength)]),
        countryId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
        email: this.builder.nonNullable.control<string>('', [Validators.required, EmailValidator.email, Validators.maxLength(PublicCheckoutFormData.emailMaxLength)]),
        firstName: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.firstNameMaxLength)]),
        lastName: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.lastNameMaxLength)]),
        phone: this.builder.control<Nillable<PhoneNumberWithPrefix>>(null, [Validators.required]),
        postCode: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.postCodeMaxLength)]),
        state: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicCheckoutFormData.stateMaxLength)]),
        streetLine1: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.streetLine1MaxLength)]),
        streetLine2: this.builder.control<Nillable<string>>('', [Validators.maxLength(PublicCheckoutFormData.streetLine2MaxLength)]),
      }),
      comment: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicCheckoutFormData.commentMaxLength)]),
      shippingAddress: this.builder.group<PublicCheckoutShippingFormControls>({
        city: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.cityMaxLength)]),
        companyName: this.builder.control<Nillable<string>>('', [Validators.maxLength(PublicCheckoutFormData.companyNameMaxLength)]),
        countryId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
        email: this.builder.nonNullable.control<string>('', [Validators.required, EmailValidator.email, Validators.maxLength(PublicCheckoutFormData.emailMaxLength)]),
        firstName: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.firstNameMaxLength)]),
        lastName: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.lastNameMaxLength)]),
        phone: this.builder.control<Nillable<PhoneNumberWithPrefix>>(null, [Validators.required]),
        postCode: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.postCodeMaxLength)]),
        state: this.builder.control<Nillable<string>>(null, [Validators.maxLength(PublicCheckoutFormData.stateMaxLength)]),
        streetLine1: this.builder.nonNullable.control<string>('', [Validators.required, Validators.maxLength(PublicCheckoutFormData.streetLine1MaxLength)]),
        streetLine2: this.builder.control<Nillable<string>>('', [Validators.maxLength(PublicCheckoutFormData.streetLine2MaxLength)]),
      }),
      shippingMethodId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      paymentMethod: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      otherShippingAddress: this.builder.nonNullable.control<boolean>(false),
    });
  }

  private stateAvailabilityChanges(
    countryControl: FormControl<Nillable<Guid>>,
  ): Observable<boolean> {
    return countryControl.valueChanges.pipe(
      startWith(countryControl.value),
      switchMap((countryId) =>
        isNil(countryId)
          ? of(null)
          : this.dictionaryCountryDataSource.fetchIsoCodeForId(countryId),
      ),
      map((code) => code !== 'PL' && !isNil(code)),
    );
  }

  private enableOrDisableShipping(enabled: boolean): void {
    if (enabled) {
      this.form.controls.shippingAddress.enable({ emitEvent: false });
    } else {
      this.form.controls.shippingAddress.disable({ emitEvent: false });
    }
  }

  private refreshShoppingCartDetailsAfterChange(change: Change): void {
    if (!this.shouldRefreshShoppingCartDetails(change)) {
      return;
    }

    this.cart.refreshDetails().pipe(once()).subscribe();
  }

  private refreshShippingMethodsAfterChange(change: Change): void {
    if (!this.shouldRefreshShippingMethods(change)) {
      return;
    }

    /* prettier-ignore */
    this.cart
      .refreshShippingMethods()
      .pipe(once())
      .subscribe({
        next: () => {
          this.form.controls.shippingMethodId.reset(null);
          this.form.controls.shippingPoint.reset(null, { emitEvent: false});
        },
      });
  }

  private refreshPaymentMethodsAfterChange(change: Change): void {
    if (!this.shouldRefreshPaymentMethods(change)) {
      return;
    }

    this.cart
      .refreshPaymentMethods()
      .pipe(once())
      .subscribe({
        next: () => {
          this.form.controls.paymentMethod.reset(null);
        },
      });
  }

  private shouldRefreshShippingMethods(change: Change): boolean {
    return [
      'billingAddress.countryId',
      'shippingAddress.countryId',
      'otherShippingAddress',
    ].includes(change.path);
  }

  private shouldRefreshPaymentMethods(change: Change): boolean {
    return ['shippingMethodId'].includes(change.path);
  }

  private shouldRefreshShoppingCartDetails(change: Change): boolean {
    return ['shippingMethodId'].includes(change.path);
  }

  private shouldExcludeChangeDetection(change: Change): boolean {
    return ['paymentMethod'].includes(change.path);
  }
}
